import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { useSummit } from "../pages/summitdetails";
import {
  RiFlaskLine, RiHeartPulseLine, RiVirusLine, RiSyringeLine,
  RiBrainLine, RiCpuLine, RiGlobalLine, RiUserHeartLine,
  RiTestTubeLine, RiMedicineBottleLine, RiHealthBookLine,
  RiMicroscopeLine, RiCalendarLine, RiArrowRightLine,
  RiBarChartBoxLine, RiShieldStarLine, RiTeamLine,
  RiMentalHealthLine, RiFocusLine, RiSettings4Line,
  RiSearchLine, RiCloseLine, RiRefreshLine, RiArrowUpLine,
  RiFilter3Line, RiAddLine, RiInformationLine, RiEyeLine
} from 'react-icons/ri';

/* ─── THEME COLORS ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

const iconMap = {
  RiFlaskLine, RiHeartPulseLine, RiVirusLine, RiSyringeLine,
  RiBrainLine, RiCpuLine, RiGlobalLine, RiUserHeartLine,
  RiTestTubeLine, RiMedicineBottleLine, RiHealthBookLine,
  RiMicroscopeLine, RiCalendarLine, RiArrowRightLine,
  RiBarChartBoxLine, RiShieldStarLine, RiTeamLine,
  RiMentalHealthLine, RiFocusLine, RiSettings4Line,
};

const CATEGORY_CONFIG = {
  'basic': { label: 'Basic Science', accent: '#2A9D8F', light: 'rgba(42,157,143,0.12)', text: '#1F7A6E' },
  'clinical': { label: 'Clinical', accent: '#3BBCAE', light: 'rgba(59,188,174,0.12)', text: '#1F7A6E' },
  'therapeutic': { label: 'Therapeutic', accent: '#D4A017', light: 'rgba(212,160,23,0.12)', text: '#B8880F' },
  'infectious': { label: 'Infectious', accent: '#218577', light: 'rgba(33,133,119,0.12)', text: '#1F7A6E' },
  'technology': { label: 'Technology', accent: '#6BC2B5', light: 'rgba(107,194,181,0.12)', text: '#218577' },
  'global': { label: 'Global Health', accent: '#D4A017', light: 'rgba(212,160,23,0.12)', text: '#B8880F' },
};

const TRACK_CONFIG = {
  'plenary': { label: 'Plenary', accent: '#D4A017' },
  'parallel': { label: 'Parallel', accent: '#2A9D8F' },
  'poster': { label: 'Poster', accent: '#3BBCAE' },
  'workshop': { label: 'Workshop', accent: '#6BC2B5' },
};

const ITEMS_PER_PAGE = 12;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Topics = () => {
  // ✅ Use context to get the real MongoDB _id — same as SubmissionGuidelines
  const { eventId } = useSummit();

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTrack, setFilterTrack] = useState('all');
  const [page, setPage] = useState(1);
  const [showBtt, setShowBtt] = useState(false);

  // ✅ Fetch topics when eventId changes — same pattern as SubmissionGuidelines
  useEffect(() => {
    if (eventId) {
      fetchActiveTopics();
    } else {
      setError("No event specified");
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // ✅ fetchActiveTopics — follows same pattern as fetchActiveGuidelines
  const fetchActiveTopics = async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ Same pattern as SubmissionGuidelines: /api/topics/active/${eventId}
      const url = `/api/topics/active/${eventId}`;
      console.log("🔍 Fetching topics from:", url);
      console.log("📌 Event ID:", eventId);

      const response = await axios.get(url);

      console.log("📦 Response:", response.data);

      if (response.data.success && response.data.topics) {
        const activeTopics = response.data.topics.filter(t => t.isActive === true);
        setTopics(activeTopics.sort((a, b) => a.order - b.order));
        setError(null);
        console.log(`✅ Loaded ${activeTopics.length} topics`);
      } else {
        setError(response.data.message || "No active topics found for this event");
        setTopics([]);
      }
    } catch (err) {
      console.error("❌ Error fetching topics:", err);
      console.error("❌ Error details:", err.response?.data);
      
      if (err.response?.status === 404) {
        setError("Topics not available for this event yet.");
      } else {
        setError(err.response?.data?.message || "Failed to load topics");
      }
      toast.error("Failed to load topics");
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const cats = [...new Set(topics.map(t => t.category).filter(Boolean))];
    return ['all', ...cats];
  }, [topics]);

  const tracks = useMemo(() => {
    const trks = [...new Set(topics.map(t => t.track).filter(Boolean))];
    return ['all', ...trks];
  }, [topics]);

  const filteredTopics = useMemo(() => {
    let filtered = [...topics];
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(t => t.category === filterCategory);
    }
    if (filterTrack !== 'all') {
      filtered = filtered.filter(t => t.track === filterTrack);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(q) || 
        t.description?.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [topics, filterCategory, filterTrack, search]);

  const paginatedTopics = filteredTopics.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredTopics.length / ITEMS_PER_PAGE);

  const getIconComponent = (iconName) => {
    const Icon = iconMap[iconName] || RiFlaskLine;
    return Icon;
  };

  const getCategoryInfo = (category) => {
    return CATEGORY_CONFIG[category] || { label: 'Research', accent: '#2A9D8F', light: 'rgba(42,157,143,0.12)', text: '#1F7A6E' };
  };

  const getTrackInfo = (track) => {
    return TRACK_CONFIG[track] || { label: 'General', accent: '#6B8090' };
  };

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterTrack('all');
    setSearch('');
    setPage(1);
  };

  // ── Loading state ── (same as SubmissionGuidelines)
  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <section style={{ padding: 'clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ padding: '5px 14px', borderRadius: 20, background: T.softTeal, border: `1px solid ${T.border}`, color: T.primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <RiMicroscopeLine size={13} style={{ display: 'inline', marginRight: 6 }} />
                Summit Topics
              </span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', fontWeight: 900, color: T.navy, letterSpacing: '-0.03em', lineHeight: 1.06, marginBottom: 14 }}>
              Conference <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Topics</em>
            </h1>
            <p style={{ fontSize: 'clamp(0.88rem, 1.3vw, 1rem)', color: T.textMid, lineHeight: 1.7, maxWidth: 560 }}>
              Loading topics...
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ color: T.textMuted }}>Loading topics...</p>
        </div>
      </div>
    );
  }

  // ── Error / empty state ── (same as SubmissionGuidelines)
  if (error && !loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <section style={{ padding: 'clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ padding: '5px 14px', borderRadius: 20, background: T.softTeal, border: `1px solid ${T.border}`, color: T.primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <RiMicroscopeLine size={13} style={{ display: 'inline', marginRight: 6 }} />
                Summit Topics
              </span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', fontWeight: 900, color: T.navy, letterSpacing: '-0.03em', lineHeight: 1.06, marginBottom: 14 }}>
              Conference <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Topics</em>
            </h1>
            <p style={{ fontSize: 'clamp(0.88rem, 1.3vw, 1rem)', color: T.textMid, lineHeight: 1.7, maxWidth: 560 }}>
The congress will provide a comprehensive platform for experts to share knowledge and collaborate. Choose from the following topics for your abstract.            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '40px 20px 60px' }}>
          <div style={{ textAlign: 'center', padding: 80, background: T.white, borderRadius: 20, border: `1px solid ${T.border}` }}>
            <div style={{ width: 60, height: 60, borderRadius: 16, background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <RiSearchLine size={28} color={T.primary} />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>No Topics Available</h3>
            <p style={{ color: T.textMuted, maxWidth: 400, margin: '0 auto' }}>{error}</p>
            <button 
              onClick={fetchActiveTopics} 
              style={{ 
                marginTop: 20, 
                padding: '10px 24px', 
                borderRadius: 8, 
                background: T.gradGold, 
                color: T.navyDeep, 
                border: 'none', 
                cursor: 'pointer', 
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <RiRefreshLine size={16} /> Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <>
      <style>{css}</style>
      <Toaster position="top-right" />

      <div style={{ minHeight: '100vh', background: T.offWhite }}>

        {/* HERO SECTION */}
        <section style={{ padding: 'clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ padding: '5px 14px', borderRadius: 20, background: T.softTeal, border: `1px solid ${T.border}`, color: T.primary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <RiMicroscopeLine size={13} style={{ display: 'inline', marginRight: 6 }} />
                Summit Topics
              </span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', fontWeight: 900, color: T.navy, letterSpacing: '-0.03em', lineHeight: 1.06, marginBottom: 14 }}>
              Conference <em style={{ color: T.accentGold, fontStyle: 'normal' }}>Topics</em>
            </h1>
            <p style={{ fontSize: 'clamp(0.88rem, 1.3vw, 1rem)', color: T.textMid, lineHeight: 1.7, maxWidth: 560 }}>
The congress will provide a comprehensive platform for experts to share knowledge and collaborate. Choose from the following topics for your abstract            </p>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>

        {/* STATS BAR */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 40px 0' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentGold }}>{topics.length}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Active Topics</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>{categories.length - 1}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Categories</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentTealLight }}>{tracks.length - 1}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Tracks</div>
            </div>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px' }}>
          <div style={{ background: T.white, borderRadius: 16, border: `1px solid ${T.border}`, padding: 20, marginBottom: 32, boxShadow: T.shadow }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                  onClick={() => { setFilterCategory('all'); setPage(1); }}
                  style={{
                    padding: '6px 16px',
                    borderRadius: 30,
                    fontSize: 13,
                    fontWeight: 500,
                    border: `1px solid ${filterCategory === 'all' ? T.primary : T.border}`,
                    background: filterCategory === 'all' ? T.primary : T.white,
                    color: filterCategory === 'all' ? T.white : T.textMid,
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  All Categories ({topics.length})
                </button>
                {categories.slice(1).map(cat => {
                  const cfg = getCategoryInfo(cat);
                  const count = topics.filter(t => t.category === cat).length;
                  return (
                    <button
                      key={cat}
                      onClick={() => { setFilterCategory(cat); setPage(1); }}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 30,
                        fontSize: 13,
                        fontWeight: 500,
                        border: `1px solid ${filterCategory === cat ? cfg.accent : T.border}`,
                        background: filterCategory === cat ? cfg.light : T.white,
                        color: filterCategory === cat ? cfg.text : T.textMid,
                        cursor: 'pointer'
                      }}
                    >
                      {cfg.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16, alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, color: T.textMuted, alignSelf: 'center' }}>Track:</span>
                <button
                  onClick={() => { setFilterTrack('all'); setPage(1); }}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    border: `1px solid ${filterTrack === 'all' ? T.primary : T.border}`,
                    background: filterTrack === 'all' ? T.primary : T.white,
                    color: filterTrack === 'all' ? T.white : T.textMid,
                    cursor: 'pointer'
                  }}
                >
                  All
                </button>
                {tracks.slice(1).map(tr => {
                  const trInfo = getTrackInfo(tr);
                  return (
                    <button
                      key={tr}
                      onClick={() => { setFilterTrack(tr); setPage(1); }}
                      style={{
                        padding: '4px 12px',
                        borderRadius: 20,
                        fontSize: 12,
                        border: `1px solid ${filterTrack === tr ? trInfo.accent : T.border}`,
                        background: filterTrack === tr ? `${trInfo.accent}15` : T.white,
                        color: filterTrack === tr ? trInfo.accent : T.textMid,
                        cursor: 'pointer'
                      }}
                    >
                      {trInfo.label}
                    </button>
                  );
                })}
              </div>

              <div style={{ position: 'relative', width: 'min(280px, 100%)' }}>
                <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.textMuted }}>
                  <RiSearchLine size={13} />
                </span>
                <input
                  type="text"
                  placeholder="Search topics..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  style={{
                    width: '100%',
                    height: 38,
                    padding: '0 36px',
                    borderRadius: 8,
                    border: `1px solid ${T.border}`,
                    background: T.white,
                    fontSize: 13,
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={e => { e.target.style.borderColor = T.primary; e.target.style.boxShadow = `0 0 0 2px ${T.primary}20`; }}
                  onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }}
                />
                {search && (
                  <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', background: 'none', border: 'none', color: T.textMuted }}>
                    <RiCloseLine size={13} />
                  </button>
                )}
              </div>
            </div>

            {(filterCategory !== 'all' || filterTrack !== 'all' || search) && (
              <div style={{ marginTop: 16 }}>
                <button onClick={clearFilters} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 11, background: `${T.accentGold}20`, color: T.accentGoldDark, border: 'none', cursor: 'pointer' }}>
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* TOPICS CARD GRID */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 60px' }}>
          {filteredTopics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 80, background: T.white, borderRadius: 20, border: `1px solid ${T.border}` }}>
              <div style={{ width: 60, height: 60, borderRadius: 16, background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <RiSearchLine size={28} color={T.primary} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>No topics found</h3>
              <p style={{ color: T.textMuted }}>Try adjusting your search or filter criteria</p>
              <button onClick={clearFilters} style={{ marginTop: 20, padding: '10px 24px', borderRadius: 8, background: T.gradGold, color: T.navyDeep, border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24 }}>
                {paginatedTopics.map((topic, index) => {
                  const Icon = getIconComponent(topic.icon);
                  const categoryInfo = getCategoryInfo(topic.category);
                  const trackInfo = getTrackInfo(topic.track);
                  
                  return (
                    <motion.div
                      key={topic._id || topic.id || `topic-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                      style={{
                        background: T.white,
                        borderRadius: 20,
                        border: `1px solid ${T.border}`,
                        overflow: 'hidden',
                        cursor: 'default',
                        transition: 'box-shadow 0.2s',
                        boxShadow: T.shadow
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = T.shadowMd}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = T.shadow}
                    >
                      <div style={{ height: 4, background: categoryInfo.accent }} />
                      <div style={{ padding: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                          <div style={{
                            width: 56,
                            height: 56,
                            borderRadius: 14,
                            background: categoryInfo.light,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 28,
                            color: categoryInfo.accent
                          }}>
                            <Icon size={28} />
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: 20,
                              fontSize: 10,
                              fontWeight: 600,
                              background: categoryInfo.light,
                              color: categoryInfo.text
                            }}>
                              {categoryInfo.label}
                            </span>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: 20,
                              fontSize: 10,
                              fontWeight: 600,
                              background: `${trackInfo.accent}15`,
                              color: trackInfo.accent
                            }}>
                              {trackInfo.label}
                            </span>
                          </div>
                        </div>
                        
                        <h3 style={{ fontSize: 18, fontWeight: 800, color: T.navy, marginBottom: 10, lineHeight: 1.3 }}>
                          {topic.name}
                        </h3>
                        
                        <p className="line-clamp-3" style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 20 }}>
                          {topic.description}
                        </p>
                        
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${T.borderLight}` }}>
                          <span style={{ fontSize: 11, color: T.textMuted }}>
                            Session: {topic.sessionType || 'Oral'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 48 }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      minWidth: 36,
                      height: 36,
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: T.white,
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      opacity: page === 1 ? 0.4 : 1
                    }}
                  >
                    <RiArrowRightLine size={14} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (page <= 3) pageNum = i + 1;
                    else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = page - 2 + i;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        style={{
                          minWidth: 36,
                          height: 36,
                          borderRadius: 8,
                          border: `1px solid ${page === pageNum ? T.accentGold : T.border}`,
                          background: page === pageNum ? T.gradGold : T.white,
                          color: page === pageNum ? T.navyDeep : T.textMid,
                          fontWeight: page === pageNum ? 600 : 400,
                          cursor: 'pointer'
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      minWidth: 36,
                      height: 36,
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: T.white,
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      opacity: page === totalPages ? 0.4 : 1
                    }}
                  >
                    <RiArrowRightLine size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* CTA SECTION */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 60px' }}>
          <div style={{
            background: T.gradNavy,
            borderRadius: 24,
            padding: '48px 56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 32,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', width: 300, height: 300, background: 'radial-gradient(circle, rgba(212,160,23,0.1), transparent)', borderRadius: '50%', top: -100, right: -80, pointerEvents: 'none' }} />
            <div>
              <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 20, background: 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.25)', color: T.accentGoldLight, fontSize: 11, fontWeight: 700, marginBottom: 12 }}>
                Abstract Submission
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', fontWeight: 900, color: T.white, marginBottom: 8 }}>
                Ready to submit your research?
              </h2>
              <p style={{ fontSize: 14, color: T.textMuted }}>Join researchers from 60+ countries presenting at the summit.</p>
            </div>
            <div>
              <Link to="/abstract-submission" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 28px',
                background: T.gradGold,
                color: T.navyDeep,
                borderRadius: 50,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: 700,
                transition: 'all 0.2s',
                boxShadow: T.shadowGold
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.shadowLg; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = T.shadowGold; }}>
                Submit Your Abstract
                <RiArrowRightLine size={15} />
              </Link>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10, textAlign: 'center' }}>Deadline: March 15, 2025</div>
            </div>
          </div>
        </div>

        {/* BACK TO TOP */}
        {showBtt && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 26,
              right: 26,
              width: 42,
              height: 42,
              borderRadius: 10,
              background: T.gradGold,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.navyDeep,
              boxShadow: T.shadowGold,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size={18} />
          </button>
        )}
      </div>
    </>
  );
};

export default Topics;